import { Check, ClipboardList } from 'lucide-react';
import { useState } from 'react';
import { Card, CardTitle } from '@/components/ui/Card';
import { useT } from '@/i18n/useT';
import { CheckinSheet, useTodayCheckin } from './CheckinSheet';

/**
 * The daily check-in entry point, reused on the home and the health screen:
 * invites when the day is empty, summarises once it's done, and opens the sheet.
 */
export function CheckinCard() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const { data } = useTodayCheckin();
  const checkin = data?.checkin;

  return (
    <>
      <Card>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex w-full items-center gap-3 text-left"
        >
          <span
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
              checkin ? 'bg-brand text-white' : 'bg-surface-2 text-brand'
            }`}
          >
            {checkin ? <Check size={20} aria-hidden /> : <ClipboardList size={20} aria-hidden />}
          </span>

          <div className="flex-1">
            <CardTitle>{checkin ? t('checkin.done') : t('checkin.today')}</CardTitle>
            <p className="mt-0.5 text-[13px] text-muted">
              {checkin ? summarise(checkin, t) : t('checkin.invite')}
            </p>
          </div>
        </button>
      </Card>

      <CheckinSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function summarise(
  checkin: NonNullable<ReturnType<typeof useTodayCheckin>['data']>['checkin'],
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  if (!checkin) return '';
  const parts: string[] = [];
  if (checkin.mood !== null) parts.push(t('checkin.summaryMood', { n: checkin.mood }));
  if (checkin.energy !== null) parts.push(t('checkin.summaryEnergy', { n: checkin.energy }));
  if (checkin.sleepHours !== null) parts.push(t('checkin.summarySleep', { n: checkin.sleepHours }));
  return parts.length > 0 ? t('checkin.summaryToday', { parts: parts.join(' · ') }) : t('checkin.summaryEdit');
}
